import { useLocale } from '../i18n/LocaleContext';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import cyberEssentials from '../assets/cyberessentials.png';
import gdpr from '../assets/gdpr.png';
import ico from '../assets/ico.png';
import nhs from '../assets/nhstoolkit.png';

const Footer = () => {
  const { t, path, appLink } = useLocale();
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <FooterContent>
        <FooterLogo>
          <Link to={path("/")}>Noteician</Link>
        </FooterLogo>

        <CertificationSection>
          <CertificationTitle>{t('footer.certifications')}</CertificationTitle>
          <CertificationLogos>
            <CertificationLogo src={cyberEssentials} alt="Cyber Essentials Certified" />
            <CertificationLogo src={gdpr} alt="GDPR Compliant" />
            <CertificationLogo src={ico} alt="ICO Registered" />
            <CertificationLogo src={nhs} alt="NHS Toolkit Compliant" />
          </CertificationLogos>
        </CertificationSection>

        <FooterLinks>
          <FooterLinkColumn>
            <FooterLinkTitle>{t('footer.product')}</FooterLinkTitle>
            <FooterLink href={appLink()} target="_blank" rel="noopener noreferrer">App</FooterLink>
            <FooterLink as={Link} to={path("/how-to-use")}>{t('nav.how')}</FooterLink>
            <FooterLink as={Link} to={path("/pricing")}>{t('footer.pricing')}</FooterLink>
          </FooterLinkColumn>

          <FooterLinkColumn>
            <FooterLinkTitle>{t('footer.company')}</FooterLinkTitle>
            <FooterLink as={Link} to={path("/contact")}>{t('nav.contact')}</FooterLink>
            <FooterLink href="mailto:founders@noteician.com">{t('footer.email')}</FooterLink>
            <FooterLink as={Link} to="/blog">Blog</FooterLink>
          </FooterLinkColumn>

          <FooterLinkColumn>
            <FooterLinkTitle>{t('footer.legal')}</FooterLinkTitle>
            <FooterLink as={Link} to={path("/legal")}>{t('footer.legalFaq')}</FooterLink>
          </FooterLinkColumn>
        </FooterLinks>
      </FooterContent>

      <FooterBottom>
        <Copyright>© {currentYear} Noteician. {t('footer.rights')}</Copyright>
      </FooterBottom>
    </FooterContainer>
  );
};

const FooterContainer = styled.footer`
  background-color: var(--light-gray);
  padding: 60px 20px 20px;
`;

const FooterContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const FooterLogo = styled.div`
  flex: 0 0 100%;
  max-width: 200px;
  margin-bottom: 30px;

  a {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--primary);
  }

  @media (max-width: 992px) {
    text-align: center;
    margin: 0 auto 30px;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  flex: 1;

  @media (max-width: 992px) {
    justify-content: center;
  }
`;

const FooterLinkColumn = styled.div`
  margin-inline-start: 60px;

  @media (max-width: 992px) {
    margin: 0 20px 30px;
    text-align: center;
  }
`;

const FooterLinkTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 15px;
  color: var(--black);
`;

const FooterLink = styled.a`
  display: block;
  margin-bottom: 10px;
  color: var(--dark-gray);
  transition: var(--transition);

  &:hover {
    color: var(--primary);
  }
`;

const FooterBottom = styled.div`
  max-width: 1200px;
  margin: 40px auto 0;
  padding-top: 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

const Copyright = styled.p`
  font-size: 0.9rem;
  color: var(--dark-gray);
  text-align: center;
`;

const CertificationSection = styled.div`
  margin-bottom: 20px;
  margin-inline-end: 40px;
  text-align: start;

  @media (max-width: 992px) {
    margin-inline-end: 0;
    margin-bottom: 30px;
    text-align: center;
  }
`;

const CertificationTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 15px;
  color: var(--black);
`;

const CertificationLogos = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 10px;

  @media (max-width: 992px) {
    justify-content: center;
  }
`;

const CertificationLogo = styled.img`
  height: 40px;
  width: auto;
  opacity: 0.8;
  transition: var(--transition);

  &:hover {
    opacity: 1;
  }
`;

export default Footer;
